import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import Modal from '@/components/modal/Modal.tsx';
import ModalHeader from '@/components/modal/ModalHeader.tsx';
import ModalContent from '@/components/modal/ModalContent.tsx';
import ModalFooter from '@/components/modal/ModalFooter.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import { debugZodResolver } from '@/lib/debugZodResolver.ts';
import {
  createConsentTemplateSchema,
  ConsentTemplateFormData,
  ConsentTemplateInputData,
} from '@/features/dashboard/consentTemplates/schemas/consentTemplateSchema.ts';
import { consentTemplateMapper } from '@/features/dashboard/consentTemplates/types/ConsentTemplateDto.mapper.ts';
import useGetConsentTemplate from '@/features/dashboard/consentTemplates/hooks/useGetConsentTemplate.ts';
import useGetConsentTemplateVariables from '@/features/dashboard/consentTemplates/hooks/useGetConsentTemplateVariables.ts';
import useUpdateConsentTemplate from '@/features/dashboard/consentTemplates/hooks/useUpdateConsentTemplate.ts';
import { getRequiredVariableKeys } from '@/features/dashboard/messageTemplates/types/templateVariables.utils.ts';
import { useInvalidSubmitFeedback } from '@/hooks/useInvalidSubmitFeedback.ts';
import ConsentTemplateForm from '@/features/dashboard/consentTemplates/components/ConsentTemplateForm.tsx';
import { ConsentTemplateFormSkeleton } from '@/features/dashboard/consentTemplates/components/ConsentTemplateFormSkeleton.tsx';
import { consentTypeLabels } from '@/features/dashboard/consentTemplates/types/ConsentTemplateType.utils.ts';

const EditConsentTemplateModal = ({
  versionId,
  onClose,
}: {
  /** Id of the template version to edit (`ConsentTemplateListDto.id`). */
  versionId: string;
  onClose: () => void;
}) => {
  // Editing content archives the current version and returns a new version id;
  // keep working against the latest one so a subsequent save doesn't hit an
  // archived (not-current) version.
  const [workingId, setWorkingId] = useState(versionId);
  const { data: template, isLoading } = useGetConsentTemplate(workingId);
  const { data: variables } = useGetConsentTemplateVariables();
  const updateMutation = useUpdateConsentTemplate();

  const resolver = useMemo(
    () =>
      debugZodResolver(
        createConsentTemplateSchema(getRequiredVariableKeys(variables)),
      ),
    [variables],
  );

  const methods = useForm<ConsentTemplateInputData>({
    resolver,
    values: template
      ? consentTemplateMapper.fromDtoToForm(template)
      : undefined,
  });

  const { handleSubmit } = methods;

  const formRef = useInvalidSubmitFeedback(methods.formState, {
    title: 'Nie można zapisać szablonu',
  });

  const onSubmit = (data: ConsentTemplateFormData) => {
    updateMutation.mutate(
      { id: workingId, data },
      {
        onSuccess: (updated) => {
          if (updated.id) setWorkingId(updated.id);
          onClose();
        },
      },
    );
  };

  return (
    <Modal
      onClose={onClose}
      isDirty={methods.formState.isDirty}
      className="max-w-[720px]"
    >
      <FormProvider {...methods}>
        <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader
            title="Edytuj szablon zgody"
            description={
              template
                ? `Rodzaj: ${consentTypeLabels[template.type]} — nie da się go zmienić po zapisaniu.`
                : undefined
            }
            onClose={onClose}
          />
          <ModalContent>
            {isLoading ? (
              <ConsentTemplateFormSkeleton />
            ) : (
              <ConsentTemplateForm />
            )}
          </ModalContent>
          <ModalFooter>
            <ActionButton
              tone="ghost"
              size="sm"
              type="button"
              onClick={onClose}
            >
              Anuluj
            </ActionButton>
            <ActionButton
              tone="accent"
              size="sm"
              type="submit"
              isLoading={updateMutation.isPending}
              disabled={isLoading}
            >
              Zapisz zmiany
            </ActionButton>
          </ModalFooter>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default EditConsentTemplateModal;
